import { BaseEvent } from "@/app/events/events";
import Color from "color";
import { DateTime, Interval } from "luxon";
import { useParams } from "react-router-dom";
import { FC, useEffect, useRef, useState } from "react";

export type CalendarWeekProps = {
  currentDateTime: DateTime;
  events: Array<any>;
  onEventClick: (event: BaseEvent) => void;
};

// Helper function to find the earliest event in the week
const getFirstEventMinuteWeek = (
  days: any[],
  items: Record<string, any[]>
): number => {
  // Default to 6 AM if no events found
  const DEFAULT_HOUR = 360; // 6 AM in minutes

  if (!days?.length || !items) {
    return DEFAULT_HOUR;
  }

  let earliestMinute = 24 * 60; // Initialize to end of day

  // Iterate through all days in the week
  for (const day of days) {
    const dayEvents = items[day.key];
    if (!dayEvents?.length) continue;

    // Check each event in the day
    for (const event of dayEvents) {
      // Fixed: Use the correct property names from your data
      const startTimeStr = event.startTime || event.timeStart;
      const startTime = DateTime.fromFormat(startTimeStr, "HH:mm:ss");
      if (!startTime.isValid) continue;

      const minutes = startTime.hour * 60 + startTime.minute;
      if (minutes < earliestMinute) {
        earliestMinute = minutes;
      }
    }
  }

  // If no valid events found or all events are at the end of day, return default
  if (earliestMinute === 24 * 60) {
    return DEFAULT_HOUR;
  }

  // Subtract 10 minutes to show some context before the first event
  return Math.max(0, earliestMinute - 10);
};

const groupBy = (array, key) => {
  if (!array) return {};
  return array.reduce((result, currentValue) => {
    (result[currentValue[key]] = result[currentValue[key]] || []).push(
      currentValue
    );
    return result;
  }, {});
};

export const CalendarWeek: FC<CalendarWeekProps> = ({
  events,
  currentDateTime,
  onEventClick,
}) => {
  const { id } = useParams();

  const container = useRef(null);
  const containerNav = useRef(null);
  const containerOffset = useRef(null);

  const [items, setItems] = useState<Record<string, any>>({});
  const [days, setDays] = useState([]);

  useEffect(() => {
    const groupedItems = groupBy(events, "date");
    setItems(groupedItems);
  }, [events]);

  useEffect(() => {
    if (!currentDateTime) {
      return;
    }

    const startOfWeek = currentDateTime.setLocale("es").startOf("week");
    const endOfWeek = currentDateTime.setLocale("es").endOf("week");

    const today = DateTime.now().startOf("day");
    const todayFormatted = today.toFormat("yyyy-MM-dd");

    const interval = Interval.fromDateTimes(startOfWeek, endOfWeek);
    const days = interval.splitBy({ day: 1 }).map((interval) => {
      const dayFormatted = interval.start
        ?.startOf("day")
        .toFormat("yyyy-MM-dd");

      const isToday = dayFormatted === todayFormatted;

      return {
        date: interval.start,
        key: dayFormatted,
        number: interval.start?.toFormat("dd"),
        displayName: interval.start?.setLocale("es").toFormat("cccc"),
        isToday: isToday,
        events: [],
      };
    });

    setDays(days);
  }, [currentDateTime]);

  useEffect(() => {
    // Set the container scroll position based on the first event of the day
    if (container.current && containerNav.current && containerOffset.current) {
      const firstEventMinute = getFirstEventMinuteWeek(days, items);

      container.current.scrollTop =
        ((container.current.scrollHeight -
          containerNav.current.offsetHeight -
          containerOffset.current.offsetHeight) *
          firstEventMinute) /
        1440;
    }
  }, [days, items]);

  return (
    <div
      ref={container}
      className="isolate flex flex-auto flex-col overflow-auto bg-white"
    >
      <div
        style={{ width: "165%" }}
        className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full"
      >
        <div
          ref={containerNav}
          className="sticky top-0 z-30 flex-none bg-white shadow ring-1 ring-black ring-opacity-5 sm:pr-8"
        >
          <div className="grid grid-cols-7 text-sm leading-6 text-gray-500 sm:hidden">
            {days?.map((day, index) => (
              <button
                key={`${day.key}_${day.number}_${index}`}
                type="button"
                className="flex flex-col items-center pb-3 pt-2"
              >
                {day.displayName}
                <span className="mt-1 flex h-8 w-8 items-center justify-center font-semibold text-gray-900">
                  {day.number}
                </span>
              </button>
            ))}
          </div>

          <div className="-mr-px hidden grid-cols-7 divide-x divide-gray-100 border-r border-gray-100 text-sm leading-6 text-gray-500 sm:grid">
            <div className="col-end-1 w-14" />
            {days?.map((day, index) => (
              <div
                key={index}
                className="flex items-center justify-center py-3"
              >
                <span className="flex gap-2">
                  {day.displayName}
                  <span className="items-center justify-center font-semibold text-gray-900">
                    {day.number}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-auto">
          <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
          <div className="grid flex-auto grid-cols-1 grid-rows-1">
            {/* Horizontal lines */}
            <div
              className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
              style={{
                gridTemplateRows: "repeat(48, minmax(3.5rem, 1fr))",
              }}
            >
              <div ref={containerOffset} className="row-end-1 h-7"></div>
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  12AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  1AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  2AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  3AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  4AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  5AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  6AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  7AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  8AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  9AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  10AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  11AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  12PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  1PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  2PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  3PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  4PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  5PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  6PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  7PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  8PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  9PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  10PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  11PM
                </div>
              </div>
              <div />
            </div>

            {/* Vertical lines */}
            <div className="col-start-1 col-end-2 row-start-1 hidden grid-cols-7 grid-rows-1 divide-x divide-gray-100 sm:grid sm:grid-cols-7">
              <div className="col-start-1 row-span-full" />
              <div className="col-start-2 row-span-full" />
              <div className="col-start-3 row-span-full" />
              <div className="col-start-4 row-span-full" />
              <div className="col-start-5 row-span-full" />
              <div className="col-start-6 row-span-full" />
              <div className="col-start-7 row-span-full" />
              <div className="col-start-8 row-span-full w-8" />
            </div>

            {/* Events */}
            <ol
              className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-8"
              style={{
                gridTemplateRows: "1.75rem repeat(288, minmax(0, 1fr)) auto",
              }}
            >
              {items && days ? (
                days?.map((day, dayIndex) => {
                  const dayEvents = items[day.key];
                  if (!dayEvents || dayEvents.length === 0) return null;

                  return dayEvents.map((item, eventIndex) => {
                    const duration = item.duration == 0 ? 15 : item.duration;
                    const isUser = item.users?.find(
                      (user) => user.id === Number(id)
                    )
                      ? true
                      : false;

                    // Fixed: Handle both property name variations
                    const startTimeStr = item.startTime || item.timeStart;
                    const endTimeStr = item.endTime || item.timeEnd;

                    const itemStartTime = DateTime.fromFormat(
                      startTimeStr,
                      "HH:mm:ss"
                    );
                    const itemEndTime = DateTime.fromFormat(
                      endTimeStr,
                      "HH:mm:ss"
                    );

                    if (!itemStartTime.isValid || !itemEndTime.isValid) {
                      console.error(
                        `Invalid time format - start: ${startTimeStr}, end: ${endTimeStr}`
                      );
                      return null;
                    }

                    const dayStartTime = DateTime.fromFormat(
                      "00:00:00",
                      "HH:mm:ss"
                    );

                    const itemStartTimeMinutes = Math.abs(
                      dayStartTime.diff(itemStartTime).as("minutes")
                    );

                    return (
                      <li
                        key={`${item.id}-${dayIndex}-${eventIndex}`}
                        onClick={() => onEventClick(item)}
                        className="relative mt-px flex"
                        style={{
                          gridColumn: `${dayIndex + 1}`, // Position in the correct day column
                          gridRow: `${itemStartTimeMinutes / 5 + 2} / span ${Math.max(
                            duration / 5,
                            3
                          )}`,
                          minHeight: "3rem",
                        }}
                      >
                        <a
                          href="#"
                          className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg p-2 text-xs leading-5 sm:p-0 sm:pl-2"
                          style={{
                            backgroundColor: Color(item.color)
                              .alpha(0.75)
                              .hexa(),
                          }}
                        >
                          <p
                            className="order-1 font-semibold cursor-pointer"
                            style={{
                              color: Color(item.color).darken(0.5).hexa(),
                            }}
                          >
                            <span
                              style={{
                                backgroundColor: isUser ? "#7CFC00" : "gray",
                              }}
                              className="bg-gray-950 w-1 h-1 p-1 rounded-full absolute top-1.5 left-2"
                            />
                            <span className="ml-3">{item?.name}</span>{" "}
                            <time dateTime="2022-01-12T06:00">
                              {startTimeStr
                                ? DateTime.fromFormat(
                                    startTimeStr,
                                    "HH:mm:ss"
                                  ).toFormat("HH:mm")
                                : ""}
                              {" - "}
                              {endTimeStr
                                ? DateTime.fromFormat(
                                    endTimeStr,
                                    "HH:mm:ss"
                                  ).toFormat("HH:mm")
                                : ""}
                            </time>
                          </p>
                        </a>
                      </li>
                    );
                  });
                })
              ) : (
                <div>Cargando...</div>
              )}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};
